import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable } from 'rxjs';
import { ConsentDateResponse } from 'src/app/shared/interfaces/consent-date-response';
import { AppConfigService } from '../../../services/app-config.service';
import { handleError } from '../../../shared/functions/error-handler';
import { IAppConfig } from '../../../shared/interfaces/app-config';
import { ISedmsFormOutput } from '../../../shared/interfaces/form/sedms-form-output.model';
import { IAssessmentPlanning } from '../interfaces/assessment-planning';
import { IProviderInfo } from '../interfaces/provider-info';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { SubmitRequestExtensionModel } from 'src/app/shared/models/request-extension/submit-request-extension-model';
import { IMeetingDetails } from 'src/app/shared/interfaces/meeting-details';
import { CancelMeeting } from '../interfaces/cancel-meeting';
import { SHMRequest } from '../models/create-shm-package/shm-request.model';
import { PreSchoolSummaryRequest } from '../models/preschool-summary/preschool-summary-request.model';
import { RequestExtensionModel } from 'src/app/shared/models/request-extension/request-extension-request-model';
import { Assessment } from '../interfaces/assessment';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { StudentDocumentDetails } from 'src/app/shared/interfaces/student-document';

@Injectable({
  providedIn: 'root',
})
export class EvaluationService <
  TGet extends ISedmsFormOutput = ISedmsFormOutput,
  TSave extends ISedmsFormOutput = TGet
> {
  config: IAppConfig;
  private reportDate: BehaviorSubject<Date | null> =
    new BehaviorSubject<Date | null>(null);
  reportDate$: Observable<Date> = this.reportDate.asObservable();

  private assessmentData : BehaviorSubject<Assessment[]> = new BehaviorSubject<Assessment[]>(null);
  assessmentData$ : Observable<Assessment[]> = this.assessmentData.asObservable();

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public submit(request: TSave, type: string): Observable<ApiResponse<TSave>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/SaveAssessments`,
      this.config.apiUrl
    );
    return this.http
      .post<ApiResponse<TSave>>(url, request)
      .pipe(catchError(handleError));
  }

  public get(id: string, type: string): Observable<IAssessmentPlanning> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/GetAssessmentPlanningByReferral/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IAssessmentPlanning>(url)
      .pipe(catchError(handleError));
  }

  public getProviders(
    providerType: number,
    languageId: number
  ): Observable<IProviderInfo[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetProviders/${encodeURIComponent(String(providerType))}/${encodeURIComponent(String(languageId))}`,
      this.config.apiUrl
    );
    return this.http
      .get<IProviderInfo[]>(url)
      .pipe(catchError(handleError));
  }

  public getEavluatorNames(
    providerType: number,
    studenSk: string
  ): Observable<IProviderInfo[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetAgencyProviders/${encodeURIComponent(String(providerType))}/${encodeURIComponent(studenSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IProviderInfo[]>(url)
      .pipe(catchError(handleError));
  }

  public getAgencyProviders(agencySk: string): Observable<IProviderInfo[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetProvidersByAgencySk/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IProviderInfo[]>(url)
      .pipe(catchError(handleError));
  }

  public getEvaluatorsByDistrict(homeDistrictSk: number): Observable<IProviderInfo[]> {
    const url: string = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetEvaluatorsByDistrict/${encodeURIComponent(String(homeDistrictSk))}`,
      this.config.apiUrl
    );
    return this.http.get<IProviderInfo[]>(url);
  }


  // TODO: This should return `Observable<SocialHistoryMeeting>` but currently getting errors.
  public getSHP(id: string): Observable<SHMRequest> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetSHP/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<SHMRequest>(url)
      .pipe(catchError(handleError));
  }

  public getSHPDocuments(id: string): Observable<StudentDocumentDetails[]> {
    return this.http
      .get<StudentDocumentDetails[]>(`${this.config.apiUrl}/Evaluations/GetSHPDocuments/${id}`)
      .pipe(catchError(handleError));
  }

  // public getSHPDocuments(id: string): Observable<StudentDocumentDetails[]> {
  //   return this.http
  //     .get<StudentDocumentDetails[]>(`${this.config.apiUrl}/Evaluations/GetSHPDocuments/${id}`)
  //     .pipe(catchError(handleError));
  // }

  // TODO: Come back to this
  public submitSHP(request: TSave): Observable<ApiResponse<SHMRequest>> {
    return this.http
      .put<ApiResponse<SHMRequest>>(`${this.config.apiUrl}/Evaluations/UpdateSHP`, request)
      .pipe(catchError(handleError));
  }

  // TODO: This should return `ApiResponse<PreSchoolSummary>` but currently getting an error. Come back to this
  public getPreSchoolSummary(id: string, type: string): Observable<PreSchoolSummaryRequest> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/GetPreSchoolSummary/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<PreSchoolSummaryRequest>(url)
      .pipe(catchError(handleError));
  }

  public getGetAssessmentDates(
    id: string,
    type: string
  ): Observable<ApiResponse<string[]>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/GetAssessmentDates/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<string[]>>(url)
      .pipe(catchError(handleError));
  }

  // TODO: This should return `ApiResponse<string>` but currently getting an error. Come back to this
  public submitPreSchoolSummary(request: TSave, type: string): Observable<PreSchoolSummaryRequest> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/AddPreSchoolSummary`,
      this.config.apiUrl
    );
    return this.http
      .post<PreSchoolSummaryRequest>(url, request)
      .pipe(catchError(handleError));
  }

  // TODO: This should return `string` but currently getting an error. Come back to this
  public getEvalutions(id: string, type: string): Observable<RequestExtensionModel> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<RequestExtensionModel>(url)
      .pipe(catchError(handleError));
  }

  // TODO: This should return `Observable<ApiResponse<Uint8Array>>` but currently getting an error. Come back to this
  public submitExtensionRequest(
    request: SubmitRequestExtensionModel
  ): Observable<RequestExtensionModel> {
    return this.http
      .post<RequestExtensionModel>(
        `${this.config.apiUrl}/Evaluations/CreateExtensionRequest`,
        request
      )
      .pipe(catchError(handleError));
  }

  public reviewAssessment(
    request: SubmitRequestExtensionModel
  ): Observable<RequestExtensionModel> {
    return this.http
      .post<RequestExtensionModel>(
        `${this.config.apiUrl}/Evaluations/ReviewAssessment`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getScheduleMeetingList(
    id: string
  ): Observable<ApiResponse<IMeetingDetails>[]> {
    const url = assertTrustedUrl(
      `${this.config.studentApiUrl}/Meeting/GetMeetingList/${encodeURIComponent(id)}`,
      this.config.studentApiUrl
    );
    return this.http
      .get<ApiResponse<IMeetingDetails>[]>(url)
      .pipe(catchError(handleError));
  }

  public submitCancelMeetingRequest(
    request: CancelMeeting
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.config.studentApiUrl}/Meeting/CancelMeeting`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getAssessmentPlanning(id: string): Observable<IAssessmentPlanning> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetAssessmentPlanning/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IAssessmentPlanning>(url)
      .pipe(catchError(handleError));
  }

  // TODO: Didn't find this endpoint in the backend
  public removeAssessment(refralSk: string, evalSk: string): Observable<ApiResponse<boolean>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/Eval/${encodeURIComponent(refralSk)}/${encodeURIComponent(evalSk)}`,
      this.config.apiUrl
    );
    return this.http
      .delete<ApiResponse<boolean>>(url)
      .pipe(catchError(handleError));
  }

  public getConsentDate(id: string): Observable<ConsentDateResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/GetConsentDate/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ConsentDateResponse>(url)
      .pipe(catchError(handleError));
  }

  public getNoticeDate(referralSk: string): Observable< { noticeDate: Date }> {
    return this.http.get< { noticeDate: Date }>(`${this.config.apiUrl}/Evaluations/NoticeDate?referralSk=${encodeURIComponent(referralSk)}`)
      .pipe(catchError(handleError));
  }


  getReportDate(data: Date): void {
    this.reportDate.next(data);
  }

  getAssessmentData(data: Assessment[]): void {
    this.assessmentData.next(data);
  }
}
